// Copyright (c) 2025 GeoVerses. All rights reserved.

import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

const BugReport = ({ open, onClose }) => {
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('Please describe the bug');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/reportBug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          description: description.trim(),
          email: email.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit bug report');
      }

      setSuccess(true);
      setDescription('');
      setEmail('');
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to submit bug report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setDescription('');
      setEmail('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Report a Bug</DialogTitle>
      <DialogContent>
        {success ? (
          <Typography variant="body1" color="success.main" sx={{ py: 2 }}>
            Thank you! Your bug report has been submitted.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Help us improve GeoVerses by reporting any issues you encounter.
            </Typography>
            <TextField
              label="Email (optional)"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              disabled={loading}
              placeholder="your@email.com"
            />
            <TextField
              label="Bug Description"
              multiline
              rows={6}
              fullWidth
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
              placeholder="Please describe what happened, what you expected, and any steps to reproduce the issue..."
            />
            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!success && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BugReport;